import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { extractPostalFromAddress } from '../../lib/demCustomers';
import { fetchOsrmRoute, geocodePostalsSequential, resolveSingaporePostal } from '../../lib/demDispatchGeocode';
import { fetchPostalCoords } from '../../lib/demDispatch';
import { addPreferredLeafletBasemap } from '../../lib/leafletRasterBasemap';

export type DeliveryMapStop = {
  id: string;
  label: string;
  kind: 'pickup' | 'dropoff';
  postal: string;
  address: string;
  title: string;
};

type ResolvedStop = DeliveryMapStop & {
  lat: number;
  lng: number;
};

type DeliveryRouteMapProps = {
  stops: DeliveryMapStop[];
  className?: string;
  /** Live driver pin from Lalamove getDriver. */
  driverLocation?: { lat: number; lng: number; label?: string } | null;
  showCoordinates?: boolean;
  tall?: boolean;
  /** When false, hide zoom controls and block scroll/pinch/dblclick zoom. */
  allowZoom?: boolean;
};

function pinSvg(label: string, fill: string): string {
  return `<svg viewBox="0 0 24 24" width="32" height="32" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${fill}" stroke="#ffffff" stroke-width="1.5"/><text x="12" y="10.5" fill="#ffffff" font-size="8.5" font-family="sans-serif" font-weight="bold" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`;
}

function markerIcon(label: string, fill: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: pinSvg(label, fill),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
}

export function DeliveryRouteMap({
  stops,
  className = '',
  driverLocation = null,
  showCoordinates = true,
  tall = false,
  allowZoom = true,
}: DeliveryRouteMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<{ markers: L.Marker[]; route?: L.Polyline; driver?: L.Marker }>({
    markers: [],
  });
  const [resolved, setResolved] = useState<ResolvedStop[]>([]);
  const [loading, setLoading] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let cancelled = false;
    const map = L.map(containerRef.current, {
      zoomControl: false,
      scrollWheelZoom: allowZoom,
      doubleClickZoom: allowZoom,
      boxZoom: allowZoom,
      touchZoom: allowZoom,
    }).setView([1.3521, 103.8198], 11);
    if (allowZoom) {
      L.control.zoom({ position: 'bottomright' }).addTo(map);
    }
    mapRef.current = map;

    void (async () => {
      await addPreferredLeafletBasemap(map);
      if (cancelled) return;
      setMapReady(true);
      window.setTimeout(() => map.invalidateSize(), 120);
    })();

    const el = containerRef.current;
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            map.invalidateSize({ animate: false });
          })
        : null;
    if (el && ro) ro.observe(el);

    return () => {
      cancelled = true;
      ro?.disconnect();
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [allowZoom]);

  useEffect(() => {
    if (stops.length === 0) {
      setResolved([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const postals = stops.map((stop) =>
            extractPostalFromAddress(stop.address, stop.postal),
          );
          const uniquePostals = [...new Set(postals.filter(Boolean))];
          const coordsMap = await geocodePostalsSequential(uniquePostals, async (postal) =>
            resolveSingaporePostal(postal, async (code) => {
              const cache = await fetchPostalCoords([code]);
              const hit = cache.get(code);
              return hit ? { lat: hit[0], lng: hit[1] } : null;
            }),
          );

          const next: ResolvedStop[] = [];
          for (let i = 0; i < stops.length; i += 1) {
            const stop = stops[i]!;
            const postal = postals[i]!;
            const coords = postal ? coordsMap.get(postal) : null;
            if (coords) {
              next.push({ ...stop, lat: coords.lat, lng: coords.lng });
            }
          }

          if (!cancelled) setResolved(next);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [stops]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const layers = layersRef.current;
    layers.markers.forEach((marker) => map.removeLayer(marker));
    layers.markers = [];
    if (layers.route) map.removeLayer(layers.route);
    layers.route = undefined;
    if (layers.driver) map.removeLayer(layers.driver);
    layers.driver = undefined;

    const bounds: L.LatLngTuple[] = [];

    for (const stop of resolved) {
      const fill = stop.kind === 'pickup' ? '#ff671f' : '#2563eb';
      const marker = L.marker([stop.lat, stop.lng], {
        icon: markerIcon(stop.label, fill),
      })
        .bindPopup(
          `<b>${stop.kind === 'pickup' ? 'Pickup' : 'Dropoff'}</b><br>${stop.title}<br>${
            stop.address || stop.postal
          }<br><code>${stop.lat.toFixed(6)}, ${stop.lng.toFixed(6)}</code>`,
        )
        .addTo(map);
      layers.markers.push(marker);
      bounds.push([stop.lat, stop.lng]);
    }

    if (
      driverLocation &&
      Number.isFinite(driverLocation.lat) &&
      Number.isFinite(driverLocation.lng)
    ) {
      layers.driver = L.marker([driverLocation.lat, driverLocation.lng], {
        icon: markerIcon('D', '#111827'),
      })
        .bindPopup(
          `<b>${driverLocation.label ?? 'Driver'}</b><br><code>${driverLocation.lat.toFixed(
            6,
          )}, ${driverLocation.lng.toFixed(6)}</code>`,
        )
        .addTo(map);
      bounds.push([driverLocation.lat, driverLocation.lng]);
    }

    if (bounds.length === 0) {
      map.setView([1.3521, 103.8198], 11);
      return;
    }

    void (async () => {
      const waypoints = resolved.map((stop) => [stop.lat, stop.lng] as [number, number]);
      const osrm = waypoints.length >= 2 ? await fetchOsrmRoute(waypoints) : null;
      if (!mapRef.current) return;

      if (osrm?.geometry.length) {
        layers.route = L.polyline(osrm.geometry, {
          color: '#ff671f',
          weight: 4,
          opacity: 0.85,
        }).addTo(map);
      } else if (waypoints.length > 1) {
        layers.route = L.polyline(waypoints, {
          color: '#64748b',
          weight: 3,
          dashArray: '6,8',
        }).addTo(map);
      }

      map.fitBounds(L.latLngBounds(bounds), { padding: [36, 36] });
    })();
  }, [resolved, mapReady, driverLocation]);

  const hasStops = stops.length > 0;
  const hasCoords = resolved.length > 0;

  return (
    <div
      className={[
        'delivery-route-map',
        tall ? 'delivery-route-map--tall' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div ref={containerRef} className="delivery-route-map__canvas" aria-label="Delivery route map" />
      {!hasStops ? (
        <div className="delivery-route-map__overlay delivery-route-map__overlay--hint">
          Enter pickup and dropoff postcodes to preview the route.
        </div>
      ) : loading ? (
        <div className="delivery-route-map__overlay delivery-route-map__overlay--hint">Updating map…</div>
      ) : !hasCoords ? (
        <div className="delivery-route-map__overlay delivery-route-map__overlay--warn">
          Add a 6-digit postal in the address to show stops on the map.
        </div>
      ) : null}
      <div className="delivery-route-map__legend">
        <span className="delivery-route-map__legend-item">
          <i className="delivery-route-map__dot delivery-route-map__dot--pickup" aria-hidden />
          Pickup
        </span>
        <span className="delivery-route-map__legend-item">
          <i className="delivery-route-map__dot delivery-route-map__dot--dropoff" aria-hidden />
          Dropoff
        </span>
        {driverLocation ? (
          <span className="delivery-route-map__legend-item">
            <i className="delivery-route-map__dot delivery-route-map__dot--driver" aria-hidden />
            Driver
          </span>
        ) : null}
      </div>
      {showCoordinates && hasCoords ? (
        <div className="delivery-route-map__coords">
          <p className="delivery-route-map__coords-title">Coordinates</p>
          <ul className="delivery-route-map__coords-list">
            {resolved.map((stop) => (
              <li key={stop.id}>
                <span className="delivery-route-map__coords-label">
                  {stop.kind === 'pickup' ? 'Pickup' : `Dropoff ${stop.label}`}
                </span>
                <code>
                  {stop.lat.toFixed(6)}, {stop.lng.toFixed(6)}
                </code>
              </li>
            ))}
            {driverLocation ? (
              <li>
                <span className="delivery-route-map__coords-label">Driver</span>
                <code>
                  {driverLocation.lat.toFixed(6)}, {driverLocation.lng.toFixed(6)}
                </code>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function buildDeliveryMapStops(
  pickup: { address: string; postal?: string; name: string },
  dropoffs: Array<{
    key: string;
    address: string;
    postal?: string;
    name: string;
    demCustomerCode: string;
  }>,
): DeliveryMapStop[] {
  const items: DeliveryMapStop[] = [];
  const pickupPostal = extractPostalFromAddress(pickup.address, pickup.postal ?? '');
  if (pickupPostal || pickup.address.trim()) {
    items.push({
      id: 'pickup',
      label: 'P',
      kind: 'pickup',
      postal: pickupPostal,
      address: pickup.address.trim(),
      title: pickup.name.trim() || 'Warehouse pickup',
    });
  }

  dropoffs.forEach((stop, index) => {
    const postal = extractPostalFromAddress(stop.address, stop.postal ?? '');
    if (!postal && !stop.address.trim()) return;
    items.push({
      id: stop.key,
      label: String(index + 1),
      kind: 'dropoff',
      postal,
      address: stop.address.trim(),
      title: stop.name.trim() || stop.demCustomerCode || `Stop ${index + 1}`,
    });
  });

  return items;
}
